/*
 * Currently there is no difference between a MAJOR & MINOR update in the code below. This is being left in for now if it is needed in the future.
 */

const majorAppVersion = import.meta.env.VITE_APP_MAJOR_VERSION;
const appVersion = import.meta.env.VITE_APP_VERSION;

import React, { useCallback, useEffect } from 'react';
import { messageSW, Workbox } from 'workbox-window';

import { sentryCaptureException, useConstructor } from '../../utilities';

interface Props {
  serviceWorkerFile: string;
  serviceWorkerScope?: string;
  serviceWorkerPollingInterval?: number; // ms
  onNewServiceWorkerUpdate: (newVersion: string) => void;
}

const PWA_UPDATE_TYPE_KEY = 'PWA_UPDATE_TYPE';
const PWA_UPDATE_VERSION_KEY = 'PWA_UPDATE_VERSION';
enum UpdateTypes {
  MAJOR = 'MAJOR',
  MINOR = 'MINOR',
}
const debug = localStorage.getItem('DEBUG_PWA') === 'true';
const DEFAULT_POLLING_INTERVAL = 30; // seconds

let registration: ServiceWorkerRegistration | undefined;
let wb: Workbox;

/**
 * This function will trigger the update to the service worker to skip waiting and then refresh the page
 *
 * @param {UpdateTypes} type
 */
const updateAndInstallServiceWorker = async (type: UpdateTypes) => {
  // eslint-disable-next-line no-console
  if (debug) console.log(`<PWAUpdate> Applying ${type} service worker update...`);

  localStorage.removeItem(PWA_UPDATE_TYPE_KEY);
  localStorage.removeItem(PWA_UPDATE_VERSION_KEY);
  try {
    if (!registration || registration.waiting === null || registration.waiting === undefined) {
      throw new Error('registration.waiting is undefined');
    }
    messageSW(registration?.waiting as ServiceWorker, { type: 'SKIP_WAITING' });
    // eslint-disable-next-line no-console
    if (debug) console.log(`<PWAUpdate> Update successful! Reloading page...`);
    await new Promise(r => setTimeout(r, 2000)); // fix: await on prev line hangs, too fast crashes safari
  } catch (error) {
    sentryCaptureException(
      error,
      '🚀 ~ file: PWAUpdate.tsx ~ line 54 ~ updateAndInstallServiceWorker ~ error',
    );
  }
  window.location.reload();
};

/**
 * Trigger the service worker to update to the latest version from outside of this component
 *
 * @return {*}
 */
export const triggerPWAUpdate = async () => {
  const type = localStorage.getItem(PWA_UPDATE_TYPE_KEY);
  if (type && (type === UpdateTypes.MAJOR || type === UpdateTypes.MINOR))
    return updateAndInstallServiceWorker(type);
};

const PWAUpdate: React.FC<Props> = ({
  serviceWorkerFile,
  serviceWorkerPollingInterval = DEFAULT_POLLING_INTERVAL * 1000,
  serviceWorkerScope = '/',
  onNewServiceWorkerUpdate,
}: Props) => {
  /**
   * Get the current PWA version based on the version.json file deployed
   *
   * @return {*}
   */
  const getPWAVersion = async () => {
    try {
      const response = await fetch('/version.json', {
        headers: { 'Cache-Control': 'no-cache', pragma: 'no-cache' },
      });
      const json = await response.json();

      if ('version' in json) {
        const { version } = json;
        // eslint-disable-next-line no-console
        if (debug) console.log(`Version from fetch: ${version}`);
        const [major, minor, patch] = version.split('.');
        return { major, minor, patch, version };
      }
      throw new Error('Unable to get version number');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Unable to get PWA version....', error);
      throw error;
    }
  };

  /**
   * Check for a new potential update to the app on the server
   *
   * @return {*}
   */
  const serviceWorkerUpdateCheck = useCallback(async () => {
    // eslint-disable-next-line no-console
    if (debug) console.log(`Checking for new service worker...`);

    // If offline, there is no need to check for a version update
    if (!navigator.onLine) {
      if (debug) console.log(`App is offline, skipping update check...`);
      return;
    }

    // Get version file
    let major;
    let version;
    try {
      ({ major, version } = await getPWAVersion());
    } catch (error) {
      console.log('🚀 ~ file: PWAUpdate.tsx ~ line 76 ~ serviceWorkerUpdateCheck ~ error', error); // eslint-disable-line no-console
      return;
    }

    // Check if newer, if same return
    if (appVersion === version) {
      // eslint-disable-next-line no-console
      if (debug) console.log(`App versions are the same... do not update`);
      return;
    }

    // Determine if major or minor update
    const updateType = major !== majorAppVersion ? UpdateTypes.MAJOR : UpdateTypes.MINOR;
    // eslint-disable-next-line no-console
    if (debug) console.log(`Update type: ${updateType}`);
    localStorage.setItem(PWA_UPDATE_TYPE_KEY, updateType);
    localStorage.setItem(PWA_UPDATE_VERSION_KEY, version);
    onNewServiceWorkerUpdate(version);

    // Check for new service worker that needs to be installed.
    if (registration && !registration.installing) {
      await registration.update();
    }
  }, [onNewServiceWorkerUpdate]);

  // Setup
  useConstructor(async () => {
    if ('serviceWorker' in navigator) {
      try {
        // Initialize workbox
        if (debug)
          // eslint-disable-next-line no-console
          console.log(`Initializing new workbox ${serviceWorkerFile} scope: ${serviceWorkerScope}`);
        wb = new Workbox(serviceWorkerFile, { scope: serviceWorkerScope });

        // Register a service worker
        // eslint-disable-next-line no-console
        if (debug) console.log('Registering service worker...');
        registration = await wb.register({ immediate: true });

        // Check for a new service worker
        await serviceWorkerUpdateCheck();
      } catch (error) {
        console.log('🚀 ~ file: PWAUpdate.tsx ~ line 30 ~ useConstructor ~ error', error); // eslint-disable-line no-console
      }
    }
  });

  // Use effect to control polling
  useEffect(() => {
    const pollServiceWorker = setInterval(() => {
      // eslint-disable-next-line no-console
      if (debug) console.log('Checking for new app update...');
      serviceWorkerUpdateCheck();
    }, serviceWorkerPollingInterval);

    return () => {
      clearInterval(pollServiceWorker);
    };
  }, [serviceWorkerPollingInterval, serviceWorkerUpdateCheck]);

  return <div data-cy="PWAUpdate" />;
};

export default PWAUpdate;
